import { elizaLogger, Action, IAgentRuntime, Memory, State, HandlerCallback } from '@elizaos/core';
import { ethers } from 'ethers';

export const listCarbonPoolsAction: Action = {
  name: 'LIST_CARBON_POOLS',
  similes: ['list pools', 'show pools', 'get pools', 'list all available pools', 'list carbon pools', 'show carbon pools', 'get carbon pools'],
  description: 'Lists all available liquidity pools from Carbon DeFi.',
  validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
    try {
      const text = message.content?.text || '';
      elizaLogger.info(`LIST_CARBON_POOLS validation - text: "${text}"`);
      const isPoolQuery = /\b(list|show|get|view|find).*(pools|liquidity|pairs)\b/i.test(text);
      elizaLogger.info(`LIST_CARBON_POOLS validation - isPoolQuery: ${isPoolQuery}`);
      return isPoolQuery;
    } catch (error) {
      elizaLogger.error('LIST_CARBON_POOLS validation error:', error);
      return false;
    }
  },
  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state: State | undefined,
    options: any = {},
    callback?: HandlerCallback
  ) => {
    elizaLogger.info('LIST_CARBON_POOLS handler called!');
    try {
      const rpcUrl = 'https://mainnet.coti.io/rpc';
      const contractAddress = '0x59f21012B2E9BA67ce6a7605E74F945D0D4C84EA';
      const carbonControllerAbi = [
        {
          inputs: [],
          name: 'pairs',
          outputs: [{ internalType: 'Token[2][]', name: '', type: 'address[2][]' }],
          stateMutability: 'view',
          type: 'function',
        },
      ];
      const erc20Abi = [
        {
          constant: true,
          inputs: [],
          name: 'symbol',
          outputs: [{ name: '', type: 'string' }],
          payable: false,
          stateMutability: 'view',
          type: 'function',
        },
      ];

      elizaLogger.info(`Connecting to providers...`);
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl, 2632500);
      

      const carbonController = new ethers.Contract(
        contractAddress,
        carbonControllerAbi,
        provider
      );

      elizaLogger.info('Fetching pairs from CarbonController contract...');
      const pairsResult = await carbonController.pairs();
      elizaLogger.info(`Found ${pairsResult.length} pairs.`);

      if (!pairsResult || pairsResult.length === 0) {
        if (callback) {
          await callback({
            text: 'No liquidity pools found on Carbon DeFi.',
            content: { success: true, data: { pools: [] } },
          });
        }
        return { text: 'No liquidity pools found.', success: true, data: { pools: [] } };
      }

      const formattedPools = await Promise.all(
        pairsResult.map(async (pair: [string, string]) => {
          try {
            const token0 = new ethers.Contract(pair[0], erc20Abi, provider);
            const token1 = new ethers.Contract(pair[1], erc20Abi, provider);
            const symbol0 = pair[0] === '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' ? 'COTI' : await token0.symbol();
            const symbol1 = pair[1] === '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' ? 'COTI' : await token1.symbol();
            return {
              pair: `${symbol0}/${symbol1}`,
              token0: pair[0],
              token1: pair[1],
            };
          } catch (error) {
            elizaLogger.warn(`Could not fetch symbol for pair ${pair[0]}/${pair[1]}`, error);
            return {
              pair: 'UNKNOWN',
              token0: pair[0],
              token1: pair[1],
            };
          }
        })
      );

      let responseText = 'Here are the available Carbon DeFi pools (token pairs):\n\n';
      for (const pool of formattedPools) {
        if (pool.pair !== 'UNKNOWN') {
          responseText += `- ${pool.pair} (${pool.token0} / ${pool.token1})\n`;
        }
      }

      if (callback) {
        await callback({
          text: responseText,
          content: { success: true, data: { pools: formattedPools } },
        });
      }

      return { text: responseText, success: true, data: { pools: formattedPools } };
    } catch (error: any) {
      elizaLogger.error('Error fetching Carbon DeFi pools:', {
        message: error.message,
        stack: error.stack.split('\n'),
      });

      if (callback) {
        await callback({
          text: `Failed to fetch Carbon DeFi pools: ${error.message}`,
          content: { success: false, error: error.message },
        });
      }

      return {
        text: `Failed to fetch Carbon DeFi pools: ${error.message}`,
        success: false,
        error: error.message,
      };
    }
  },
  examples: [],
};

